import { useFlags } from "launchdarkly-react-client-sdk";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useParams } from "react-router-dom";

import AttachFileIcon from "@mui/icons-material/AttachFile";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Collapse from "@mui/material/Collapse";
import FormControlLabel from "@mui/material/FormControlLabel";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import useTheme from "@mui/material/styles/useTheme";

import { vestResolver } from "@hookform/resolvers/vest";
import { merge } from "lodash";

import { useAppDispatch, useAppSelector } from "app/hooks";
import CheckboxFormField from "components/checkbox-form-field";
import ErrorSnackbarContent from "components/error-snackbar-content";
import FileUpload from "components/file-upload";
import FileUploadComputerAndEDocs from "components/file-upload-with-edocs";
import { CloseIcon } from "components/icons";
import InputFormField from "components/input-form-field";
import ModalForm from "components/modal-form";
import WordProcessorField from "components/word-processor-field";
import { UPLOAD_FILES_FROM_EDOCS_FEATURE } from "constants/feature-flags";
import { FAX_TEMPLATE_TYPES } from "constants/templates";
import {
  selectCurrentClinic,
  selectCurrentUser
} from "features/auth/auth-slice";
import { useSendFaxMutation } from "services/faxes";
import type { Patient } from "services/types/patients.type";
import { Physician } from "services/types/physician.type";
import {
  convertHtmlToText,
  formatBytes,
  formatPhoneNumber,
  getFullName
} from "utils/common";
import { MAX_FILE_SIZE } from "utils/validations.utils";

import {
  setCustomMessage,
  setOpen
} from "../action-snackbar/action-snackbar-slice";
import PatientFieldWithSearch from "../patient-field-with-search";
import PhysicianField from "../physician-field";
import { faxFormValidator } from "./fax-form-validator";
import FaxNumberField from "./fax-number-field.component";

export const PATIENT_SOURCE = "patient";
export const UPLOAD_SOURCE = "upload";

type FaxFormProps = {
  patient?: any;
  isOpen: boolean;
  onClose: () => void;
  selectedFiles?: any[];
  initialDocuments?: File[];
  source?: string;
};

export interface FaxFormValues {
  recipient: string;
  physician?: Physician;
  documents: FileList | null;
  source: string;
  include_cover_page: boolean;
  notes: string;
  subject: string;
  recipient_name: string;
  sender_name: string;
  patient: any | null;
}

const DEFAULT_VALUES: FaxFormValues = {
  recipient: "",
  documents: null,
  source: UPLOAD_SOURCE,
  include_cover_page: true,
  notes: "",
  subject: "",
  recipient_name: "",
  sender_name: "",
  patient: null
};

export default function FaxForm({
  isOpen,
  onClose,
  patient,
  selectedFiles,
  initialDocuments,
  source = UPLOAD_SOURCE
}: FaxFormProps) {
  const params = useParams();
  const flags = useFlags();
  const isUploadFilesFromEDocsEnabled = flags[UPLOAD_FILES_FROM_EDOCS_FEATURE];
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const currentClinic = useAppSelector(selectCurrentClinic);
  const currentUser = useAppSelector(selectCurrentUser);
  const [sendFax, { isLoading }] = useSendFaxMutation();
  const [uploadWarningMessage, setUploadWarningMessage] = useState("");
  const [faxUploadOverride, setFaxUploadOverride] = useState(false);

  const patientId =
    Number(String(params["*"] ?? "").split("/")[0]) || undefined;

  const subjectValue = patient
    ? `${patient?.first_name} ${patient?.last_name}`
    : (currentClinic?.display_name ?? currentClinic?.practice?.name);

  const defaultValues: FaxFormValues = useMemo(() => {
    if (!isOpen) return DEFAULT_VALUES;
    return merge({}, DEFAULT_VALUES, {
      source,
      documents: initialDocuments,
      patient: patient,
      subject: subjectValue,
      sender_name: `${getFullName({
        firstName: currentUser.first_name || "",
        lastName: currentUser.last_name || "",
        title: currentUser.title,
        suffix: currentUser.suffix
      })}`,
      physician: patient?.referring_physician || undefined
    });
  }, [isOpen, source, patient, currentUser, initialDocuments, subjectValue]);

  const formMethods = useForm<FaxFormValues>({
    mode: "onChange",
    resolver: vestResolver(faxFormValidator(), undefined, {
      mode: "sync"
    }),
    defaultValues
  });

  const { control, reset, handleSubmit, trigger, setValue, watch } =
    formMethods;

  useEffect(() => {
    reset(defaultValues);
    trigger("subject");
  }, [defaultValues, reset, trigger]);

  const [physician, includeCoverPage, currentPatient] = watch([
    "physician",
    "include_cover_page",
    "patient"
  ]);

  const handleChangePatient = useCallback(
    (patient: Patient | null) => {
      setValue("patient", patient);
    },
    [setValue]
  );

  useEffect(() => {
    if (!includeCoverPage) {
      setValue("notes", "");
      setValue("subject", "");
      setValue("recipient_name", "", { shouldValidate: true });
      setValue("sender_name", "", { shouldValidate: true });
    }
  }, [includeCoverPage, setValue]);

  useEffect(() => {
    if (physician) {
      setValue(
        "recipient",
        physician?.fax_phone
          ? formatPhoneNumber(physician?.fax_phone) || ""
          : ""
      );

      if (includeCoverPage) {
        setValue(
          "recipient_name",
          `${getFullName({
            firstName: physician.first_name || "",
            lastName: physician.last_name || "",
            title: physician.title,
            suffix: physician.suffix
          })}`,
          { shouldValidate: true }
        );

        setValue("subject", subjectValue, {
          shouldValidate: true
        });
      }
      trigger();
    }
  }, [
    physician,
    setValue,
    includeCoverPage,
    patient,
    currentClinic,
    trigger,
    subjectValue
  ]);

  const modalCloseHandler = useCallback(() => {
    reset();
    onClose();
    setFaxUploadOverride(false);
    setUploadWarningMessage("");
  }, [reset, onClose, setFaxUploadOverride, setUploadWarningMessage]);

  const patientPrimaryCarePhysician =
    watch("patient")?.primary_care_physician?.full_name;
  const patientReferringPhysician =
    watch("patient")?.referring_physician?.full_name;

  return (
    <ModalForm
      maxWidth="lg"
      isOpen={isOpen}
      isSubmitting={isLoading}
      onCancel={modalCloseHandler}
      onSubmit={handleSubmit(
        ({
          recipient,
          documents,
          include_cover_page,
          notes,
          subject,
          recipient_name,
          sender_name,
          patient
        }) => {
          if (
            source === UPLOAD_SOURCE &&
            documents?.length === 0 &&
            !faxUploadOverride
          ) {
            setUploadWarningMessage(
              "Are you sure you want to send a fax without a document?"
            );
            setFaxUploadOverride(true);
            return;
          }
          sendFax({
            include_cover_page,
            patient: patient?.id,
            notes: include_cover_page
              ? convertHtmlToText(notes).trim()
              : undefined,
            subject: include_cover_page ? subject : undefined,
            recipient_name: include_cover_page ? recipient_name : undefined,
            sender_name: include_cover_page ? sender_name : undefined,
            recipient: recipient.replace(/\D/g, ""),
            clinic: Number(currentClinic?.id),
            ...(source === UPLOAD_SOURCE
              ? { documents }
              : {
                  box_file_ids: selectedFiles?.map((item) => item.id).join(",")
                })
          }).then((response: any) => {
            if ("error" in response) {
              dispatch(
                setCustomMessage(<ErrorSnackbarContent response={response} />)
              );
              dispatch(setOpen(true));
            } else {
              dispatch(setCustomMessage("Fax Sent"));
              dispatch(setOpen(true));
              modalCloseHandler();
            }
          });
        }
      )}
      title={`Send Fax${patient ? ` for ${patient.fullName}` : ""}`}
      hideBackdrop={false}
      formName="product"
      control={control}
      submitButtonLabel={faxUploadOverride ? "Send Anyway" : "Send"}
    >
      <FormProvider {...formMethods}>
        {(patientPrimaryCarePhysician || patientReferringPhysician) && (
          <Box display="flex" mb={2} justifyContent="space-between" gap={1}>
            {patientPrimaryCarePhysician && (
              <Box display="flex" alignItems="center" gap={0.5}>
                <Typography fontWeight={700}>
                  Primary Care Physician:
                </Typography>
                <Typography>{patientPrimaryCarePhysician}</Typography>
              </Box>
            )}

            {patientReferringPhysician && (
              <Box display="flex" alignItems="center" gap={0.5}>
                <Typography fontWeight={700}>Referring Physician:</Typography>
                <Typography>{patientReferringPhysician}</Typography>
              </Box>
            )}
          </Box>
        )}

        <Collapse in={Boolean(uploadWarningMessage)}>
          <Alert
            severity="warning"
            action={
              <IconButton
                aria-label="close"
                color="inherit"
                size="small"
                onClick={() => {
                  setUploadWarningMessage("");
                }}
              >
                <CloseIcon fontSize="inherit" />
              </IconButton>
            }
            sx={{ mb: 1 }}
          >
            {uploadWarningMessage}
          </Alert>
        </Collapse>
        <Grid
          container
          width="100%"
          rowSpacing={2}
          sx={{ boxSizing: "border-box" }}
        >
          <Grid item xs={6} pr={2}>
            <PhysicianField
              name="physician"
              label="Physician"
              control={control}
              showIcon
              sx={{ ml: -4 }}
            />
          </Grid>
          <Grid item xs={6} pl={2}>
            <FaxNumberField
              name="recipient"
              label="Fax Number"
              control={control}
              required
            />
          </Grid>
          <Grid item xs={12}>
            <FormControlLabel
              label="Include cover page"
              control={
                <CheckboxFormField
                  name="include_cover_page"
                  control={control}
                  testId="include_cover_page"
                  onClick={(checked) => {
                    if (checked) {
                      setValue("subject", subjectValue);

                      setValue(
                        "sender_name",
                        `${currentUser?.first_name} ${currentUser?.last_name}`,
                        { shouldValidate: true }
                      );
                    }
                  }}
                />
              }
            />
          </Grid>

          {includeCoverPage && (
            <Grid item xs={12}>
              <InputFormField
                name="subject"
                label="Subject"
                control={control}
                sx={{ ml: 0 }}
                inputProps={{ maxLength: 55 }}
              />
            </Grid>
          )}

          {includeCoverPage && (
            <>
              <Grid item xs={6} pr={2}>
                <InputFormField
                  name="recipient_name"
                  label="Recipient Name"
                  control={control}
                  sx={{ ml: 0 }}
                  required
                />
              </Grid>
              <Grid item xs={6} pl={2}>
                <InputFormField
                  name="sender_name"
                  label="Sender Name"
                  control={control}
                  sx={{ ml: 0 }}
                  required
                />
              </Grid>
            </>
          )}

          <Grid item xs={12}>
            <PatientFieldWithSearch
              label="Which patient is this efax associated to"
              placeholder="Search Patient"
              disabled={Boolean(patient)}
              control={control}
            />
          </Grid>

          {includeCoverPage && (
            <Grid item xs={12}>
              <WordProcessorField
                name="notes"
                control={control}
                placeholder="Notes"
                height={240}
                featuresGroup="textOnly"
                allowTemplates
                allowMergeTags
                allowInsertMergeTagsValues
                templateTypes={FAX_TEMPLATE_TYPES}
                allowToolbarAutohide={false}
                allowHtml={false}
                allowPasteAsText={false}
                mergeTagsPatientId={
                  Number(currentPatient?.id) || patientId || -1
                }
                maxLength={4000}
              />
            </Grid>
          )}

          <Grid item xs={12}>
            {source === UPLOAD_SOURCE &&
              (isUploadFilesFromEDocsEnabled ? (
                <FileUploadComputerAndEDocs
                  name="documents"
                  direction="row"
                  setUploadWarningMessage={setUploadWarningMessage}
                  patient={currentPatient}
                  onPatientSelected={handleChangePatient}
                  acceptedType="application/pdf"
                />
              ) : (
                <FileUpload
                  name="documents"
                  label="Upload up to 10 PDF files"
                  acceptedType="application/pdf"
                  fileSizeLimit={MAX_FILE_SIZE}
                  setUploadWarningMessage={setUploadWarningMessage}
                  fileCountLimit={10}
                />
              ))}
            {source === PATIENT_SOURCE &&
              selectedFiles?.map((item) => {
                return (
                  <Box
                    key={item.id}
                    sx={{
                      position: "relative",
                      backgroundColor: `${theme.palette.info.light}10`,
                      borderRadius: 1.5,
                      px: 0.5,
                      py: 1,
                      mb: 1
                    }}
                  >
                    <Box display="flex">
                      <Box display="flex" sx={{ alignItems: "center" }}>
                        {item.thumbnailUrl ? (
                          <img
                            style={{ height: "24px", width: "24px" }}
                            src={item.thumbnailUrl}
                            alt="Preview"
                          />
                        ) : (
                          <AttachFileIcon
                            sx={{ height: "24px", width: "24px" }}
                          />
                        )}
                      </Box>

                      <Box sx={{ ml: 1, maxWidth: "calc(100% - 50px)" }}>
                        <Typography sx={{ mb: 1 }}>{item.name}</Typography>
                        <Typography variant="body2">
                          {formatBytes(item.size)}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                );
              })}
          </Grid>
        </Grid>
      </FormProvider>
    </ModalForm>
  );
}
